import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { getHot, searchVideos } from "@/data/videos";
import VideoCard from "@/components/base/VideoCard";
import SectionHeading from "@/components/base/SectionHeading";

type Tab = "all" | "movie" | "tv";

const tabs: { value: Tab; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "movie", label: "电影" },
  { value: "tv", label: "电视剧" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get("q") || "").trim();
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<Tab>("all");

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useDocumentMeta({
    title: keyword ? `“${keyword}”的搜索结果 - 51无敌电影网` : "搜索影视 - 51无敌电影网",
    description: keyword
      ? `在51无敌电影网搜索“${keyword}”相关的电影和电视剧，快速定位你想看的影视作品。`
      : "在51无敌电影网搜索电影、电视剧及演员，快速找到你想看的影视作品。",
    keywords: "影视搜索,电影搜索,电视剧搜索,演员搜索,51无敌电影网",
  });

  const results = useMemo(() => (keyword ? searchVideos(keyword) : []), [keyword]);
  const movieResults = results.filter((item) => item.type === "movie");
  const tvResults = results.filter((item) => item.type === "tv");
  const recommendations = useMemo(() => getHot(6), []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { q: value } : {});
    setTab("all");
  };

  const showMovies = tab === "all" || tab === "movie";
  const showTv = tab === "all" || tab === "tv";

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <div className="max-w-3xl">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">影视搜索</h1>
        <p className="mt-2 text-sm text-foreground-500">支持按影视标题、演员、导演与类型关键字查找</p>

        <form onSubmit={handleSubmit} className="mt-5">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
              <input
                type="text"
                name="q"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入影片名、演员或类型，例如：星际、悬疑"
                className="w-full h-12 pl-11 pr-4 rounded-lg bg-background-50 border border-background-200 text-sm md:text-base text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="h-12 px-7 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-semibold flex items-center justify-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line"></i>
              搜索
            </button>
          </div>
        </form>
      </div>

      {keyword && (
        <div className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
            <p className="text-sm text-foreground-500">
              搜索关键词：<span className="font-semibold text-foreground-900">“{keyword}”</span>
              <span className="mx-2">·</span>
              共找到 <span className="font-semibold text-primary-600">{results.length}</span> 个结果
            </p>
            <div className="flex items-center gap-1.5 p-1 rounded-full bg-background-100">
              {tabs.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setTab(item.value)}
                  className={`px-4 py-1.5 rounded-full text-xs font-label transition-colors cursor-pointer whitespace-nowrap ${
                    tab === item.value
                      ? "bg-background-50 text-primary-600 font-semibold shadow-sm"
                      : "text-foreground-500 hover:text-foreground-800"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {results.length > 0 ? (
            <div className="space-y-10">
              {showMovies && movieResults.length > 0 && (
                <section>
                  <SectionHeading icon="ri-film-line" title="相关电影" subtitle={`${movieResults.length} 个结果`} />
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                    {movieResults.map((item) => (
                      <VideoCard key={item.id} item={item} />
                    ))}
                  </div>
                </section>
              )}
              {showTv && tvResults.length > 0 && (
                <section>
                  <SectionHeading icon="ri-tv-2-line" title="相关电视剧" subtitle={`${tvResults.length} 个结果`} />
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                    {tvResults.map((item) => (
                      <VideoCard key={item.id} item={item} />
                    ))}
                  </div>
                </section>
              )}
              {((tab === "movie" && movieResults.length === 0) ||
                (tab === "tv" && tvResults.length === 0)) && (
                <div className="py-16 text-center rounded-lg bg-background-100">
                  <p className="text-foreground-600">该分类下暂无匹配结果，试试切换“全部”查看</p>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg bg-background-100 py-16 px-6 text-center">
              <span className="w-16 h-16 rounded-full bg-background-200 text-foreground-400 flex items-center justify-center mx-auto">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <p className="mt-4 text-foreground-700 font-medium">没有找到与“{keyword}”相关的影视作品</p>
              <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或看看下面的热门推荐</p>
              <Link
                to="/category/movie"
                className="mt-5 inline-flex px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览全部电影
              </Link>
            </div>
          )}
        </div>
      )}

      {!keyword && (
        <div className="mt-10">
          <SectionHeading icon="ri-fire-line" title="热门推荐" subtitle="不知道看什么？先看看大家都在追的" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {recommendations.map((item) => (
              <VideoCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}

      {keyword && results.length > 0 && (
        <section className="mt-12">
          <SectionHeading icon="ri-magic-line" title="猜你喜欢" subtitle="更多值得一看的影视推荐" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {recommendations.map((item) => (
              <VideoCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}